import { useEffect, useMemo, useState } from "react";

import {
  CameraIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import usePartMutations from "../../data/usePartMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IPart, IPartImageMpnAnalysis, IMpnImageAnalysisResult, IPartImageType } from "../../models/Part";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import { ebaySearchString } from "../../utils/ebaySearchString";
import UnlistedKTypeUpdate from "../ebayListings/unlistedTable/UnlistedKTypeUpdate";
import PartProLookupButton from "../shared/PartProLookupButton";
import Tooltip from "../shared/Tooltip";
import StockMpnImageModal from "./StockMpnImageModal";
import StockMpnAnalysisModal from "./StockMpnAnalysisModal";

type SimpleField = {
  key: "oePartNumber" | "partNumber";
  label: string;
  tooltip: string;
  value: string;
  setValue: (v: string) => void;
  previous: string | null;
};

const parseSuperseded = (raw: string | null | undefined): string[] =>
  (raw ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const serializeSuperseded = (list: string[]): string =>
  list
    .map((s) => s.trim())
    .filter(Boolean)
    .join(", ");

export default function StockMpnCell({
  part,
  // Opt-in: the eBay listing tables surface a PartPro lookup next to the OE box;
  // the stock table leaves it off.
  showPartProLookup = false,
}: {
  part: IPart;
  showPartProLookup?: boolean;
}) {
  const { t } = useTranslation("stock");
  const { patchPart, getMpnImageAnalysis, processMpnImage } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Number");
  const [oe, setOe] = useState(part.oePartNumber ?? "");
  const [mpn, setMpn] = useState(part.partNumber ?? "");
  const [supersededList, setSupersededList] = useState<string[]>(() => {
    const parsed = parseSuperseded(part.supersededPartNumbers);
    return parsed.length > 0 ? parsed : [""];
  });
  const [imageOpen, setImageOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState<IPartImageMpnAnalysis | null>(null);

  const mpnImage = useMemo(
    () => part.images?.find((image) => image.imageType === IPartImageType.MPN),
    [part.images]
  );
  useEffect(() => setOe(part.oePartNumber ?? ""), [part.oePartNumber]);
  useEffect(() => setMpn(part.partNumber ?? ""), [part.partNumber]);
  useEffect(() => {
    const parsed = parseSuperseded(part.supersededPartNumbers);
    setSupersededList(parsed.length > 0 ? parsed : [""]);
  }, [part.supersededPartNumbers]);

  const commitSimple = (key: SimpleField["key"], next: string, previous: string | null) => {
    const trimmed = next.trim();
    const prev = previous ?? "";
    if (trimmed === prev) return;

    patchPart.mutate(
      { id: part.id, [key]: trimmed === "" ? null : trimmed },
      {
        ...saveHandlers,
        onSuccess: () => {
          if (key === "oePartNumber") part.oePartNumber = trimmed === "" ? null : trimmed;
          if (key === "partNumber") part.partNumber = trimmed === "" ? null : trimmed;
          saveHandlers.onSuccess();
        },
      }
    );
  };

  const commitSuperseded = (next: string[]) => {
    const serialized = serializeSuperseded(next);
    const previous = serializeSuperseded(parseSuperseded(part.supersededPartNumbers));
    if (serialized === previous) return;

    patchPart.mutate(
      { id: part.id, supersededPartNumbers: serialized === "" ? null : serialized },
      {
        ...saveHandlers,
        onSuccess: () => {
          part.supersededPartNumbers = serialized === "" ? null : serialized;
          saveHandlers.onSuccess();
        },
      }
    );
  };

  const updateSupersededAt = (index: number, next: string) => {
    setSupersededList((prev) => {
      const copy = [...prev];
      copy[index] = next;
      return copy;
    });
  };

  const commitSupersededAt = (index: number) => {
    const current = [...supersededList];
    current[index] = current[index].trim();
    commitSuperseded(current);
  };

  const handleOpenAnalysis = (analysisId?: string | null) => {
    setAnalysisOpen(true);
    setAnalysisData(null);

    if (!analysisId) return;

    getMpnImageAnalysis.mutate(analysisId, {
      onSuccess: (response) => {
        setAnalysisData(response.data);
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const mapAnalysisResultToModel = (result: IMpnImageAnalysisResult): IPartImageMpnAnalysis => ({
    id: "",
    created: "",
    updated: null,
    partImageId: mpnImage?.id ?? "",
    partDataId: part.id,
    foundMpn: result.foundMpn,
    bestMatchMpn: result.bestMatchMpn,
    candidateMpns: result.candidateMpns?.join(",") ?? "",
    confidence: result.confidence,
    manufacturer: result.manufacturer,
    details: result.details,
  });

  const handleRunMpnLookup = () => {
    if (!mpnImage?.id) return;

    setAnalysisData(null);
    processMpnImage.mutate(mpnImage.id, {
      onSuccess: (response) => {
        const result = response.data;
        if (!result) return;

        setAnalysisData(mapAnalysisResultToModel(result));
        setAnalysisOpen(true);

        if (!part.partNumber && result.bestMatchMpn && result.confidence >= 70) {
          part.partNumber = result.bestMatchMpn;
          part.partNumberAddedByAi = true;
          setMpn(result.bestMatchMpn);
        }
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const addSuperseded = () => {
    setSupersededList((prev) => [...prev, ""]);
  };

  const removeSuperseded = (index: number) => {
    const next = supersededList.filter((_, i) => i !== index);
    const withFallback = next.length > 0 ? next : [""];
    setSupersededList(withFallback);
    commitSuperseded(next);
  };

  const simpleFields: SimpleField[] = [
    {
      key: "oePartNumber",
      label: "OE",
      tooltip: t("OE Part Number"),
      value: oe,
      setValue: setOe,
      previous: part.oePartNumber,
    },
    {
      key: "partNumber",
      label: "MPN",
      tooltip: t("Manufacturer Part Number"),
      value: mpn,
      setValue: setMpn,
      previous: part.partNumber,
    },
  ];

  const inputClass = classNames(
    "w-full min-w-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs text-black",
    "focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
  );
  const disabledInputClass = classNames(
    inputClass,
    "cursor-not-allowed bg-slate-50 text-slate-500 focus:border-slate-200 focus:ring-0"
  );

  return (
    <>
      <StockMpnImageModal
        open={imageOpen}
        setOpen={setImageOpen}
        part={part}
        mpn={mpn}
        setMpn={setMpn}
        onMpnBlur={(value) => commitSimple("partNumber", value, part.partNumber)}
      />
      <StockMpnAnalysisModal
        open={analysisOpen}
        setOpen={setAnalysisOpen}
        analysisData={analysisData}
        isLoading={getMpnImageAnalysis.isLoading || processMpnImage.isLoading}
        mpnImage={mpnImage}
        currentMpn={mpn}
        partNumberAddedByAi={part.partNumberAddedByAi}
        canRunLookup={!!mpnImage?.id}
        onRunLookup={handleRunMpnLookup}
        onApplyMpn={(value) => {
          setMpn(value);
          commitSimple("partNumber", value, "");
        }}
      />

      <div
        className="flex flex-col gap-1"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {simpleFields.map((field) => (
          <label
            key={field.key}
            className={classNames(
              "flex gap-1.5 text-xs text-black",
              field.key === "partNumber" ? "items-start" : "items-center"
            )}
          >
            <Tooltip content={field.tooltip} className="w-8 flex-none">
              <span className="font-semibold uppercase tracking-wide text-black">
                {field.label}
              </span>
            </Tooltip>
            <div className="w-full">
              <input
                type="text"
                value={field.value}
                readOnly={field.key === "oePartNumber"}
                disabled={field.key === "oePartNumber"}
                onChange={(e) => field.key === "partNumber" && field.setValue(e.target.value)}
                onBlur={(e) => field.key === "partNumber" && commitSimple(field.key, e.target.value, field.previous)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                className={field.key === "oePartNumber" ? disabledInputClass : inputClass}
              />
              {field.key === "partNumber" && (
                <div className="flex items-center gap-1 mt-0.5">
                  <button
                    type="button"
                    onClick={() => mpnImage && setImageOpen(true)}
                    disabled={!mpnImage}
                    title={mpnImage ? t("View MPN image") : t("No MPN image")}
                    className={classNames(
                      "flex-none rounded p-0.5 transition-colors",
                      mpnImage
                        ? "text-primary-600 hover:bg-primary-50"
                        : "cursor-not-allowed text-slate-300"
                    )}
                  >
                    <CameraIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOpenAnalysis(mpnImage?.partImageMpnAnalysisId)}
                    disabled={!mpnImage?.id}
                    title={
                      mpnImage?.id
                        ? t("View MPN image analysis")
                        : t("No MPN image")
                    }
                    className={classNames(
                      "relative flex-none rounded p-0.5 transition-colors",
                      mpnImage?.id
                        ? "text-primary-600 hover:bg-primary-50"
                        : "cursor-not-allowed text-slate-300"
                    )}
                  >
                    <SparklesIcon className="h-4 w-4" />
                    {part.partNumberAddedByAi && (
                      <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-rose-500" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      field.value.trim() && openInNewTab(ebaySearchString(field.value.trim()))
                    }
                    disabled={!field.value.trim()}
                    title={t("Research MPN on eBay")}
                    className={classNames(
                      "flex-none rounded p-0.5 transition-colors",
                      "text-primary-600 hover:bg-primary-50",
                      "disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                    )}
                  >
                    <img src="/ebayLogo.svg" alt="eBay" className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      field.value.trim() &&
                      openInNewTab(
                        `https://www.google.com/search?q=${encodeURIComponent(field.value.trim())}`
                      )
                    }
                    disabled={!field.value.trim()}
                    title={t("Research MPN on Google")}
                    className={classNames(
                      "flex-none rounded p-0.5 transition-colors",
                      "text-primary-600 hover:bg-primary-50",
                      "disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                    )}
                  >
                    <MagnifyingGlassIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            {field.key === "oePartNumber" && showPartProLookup && (
              <PartProLookupButton part={part} />
            )}
            {field.key === "oePartNumber" && (
              <button
                type="button"
                onClick={() =>
                  field.value.trim() && openInNewTab(ebaySearchString(field.value.trim()))
                }
                disabled={!field.value.trim()}
                title={t("Research OE on eBay")}
                className={classNames(
                  "flex-none rounded p-0.5 transition-colors",
                  "text-primary-600 hover:bg-primary-50",
                  "disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                )}
              >
                <img src="/ebayLogo.svg" alt="eBay" className="h-4 w-4" />
              </button>
            )}
          </label>
        ))}

        {supersededList.map((entry, index) => (
          <label
            key={`sup-${index}`}
            className="flex items-center gap-1.5 text-xs text-black"
          >
            {index === 0 ? (
              <Tooltip content={t("Superceded Part Number")} className="w-8 flex-none">
                <span className="font-semibold uppercase tracking-wide text-black">
                  SUP
                </span>
              </Tooltip>
            ) : (
              <span className="w-8 flex-none" />
            )}
            <input
              type="text"
              value={entry}
              onChange={(e) => updateSupersededAt(index, e.target.value)}
              onBlur={() => commitSupersededAt(index)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className={inputClass}
            />
            {index === supersededList.length - 1 ? (
              <button
                type="button"
                onClick={addSuperseded}
                title={t("Add another superseded MPN")}
                className="flex-none rounded p-0.5 text-primary-600 transition-colors hover:bg-primary-50"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => removeSuperseded(index)}
                title={t("Remove this superseded MPN")}
                className="flex-none rounded p-0.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </label>
        ))}

        <UnlistedKTypeUpdate part={part} />
      </div>
    </>
  );
}
